import type { transitionHookFn } from "app/routes/handlers/redirectFunctions/redirectPipe";
import type { Transition } from "@uirouter/core";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import {
    EUrlKeys,
    saveFilterConsistencyAfterFirstNavigation,
    shouldApplyFilterConsistency,
    testAndApplyChanges,
} from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { SwRedux } from "app/services/SwRedux";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getDurationRequiredChanges } from "app/routes/handlers/filter-consistency/handlers/duration/durationFilterConsistencyHandler";
import { getOpenCountryFilterRequiredChanges } from "app/routes/handlers/filter-consistency/handlers/country/openCountryFilterConsistencyHandler";
import { isEmpty } from "lodash";
import { getWebSourceIndustryRequiredChanges } from "app/routes/handlers/filter-consistency/handlers/webSource/webSourceFilterConsistencyIndustryHandler";

export const onRedirectIndustryAnalysisLeaders = (): transitionHookFn => {
    return (transition: Transition) => {
        const fromState = transition.from() as IRouterState;
        const toParams = transition.params("to");

        if (shouldApplyFilterConsistency(fromState, toParams, EUrlKeys.INDUSTRY)) {
            const swRedux = SwRedux.getInstance();
            const targetState = transition.to() as IRouterState;
            const {
                digitalSuiteSelectedCountry,
                digitalSuiteOpenSelectedCountry,
                digitalSuiteSelectedDuration,
                digitalSuiteSelectedWebSource,
                isNewSession,
            } = swRedux.getState().usersDigitalSuiteFiltersSelection;
            if (!isNewSession) {
                const componentConfigId = proNavigator.getConfigIdFromStateConfig(targetState, toParams);

                const durationChanges = getDurationRequiredChanges(
                    componentConfigId,
                    digitalSuiteSelectedDuration,
                    targetState,
                    toParams,
                );

                const countryChanges = getOpenCountryFilterRequiredChanges(
                    componentConfigId,
                    digitalSuiteSelectedCountry,
                    digitalSuiteOpenSelectedCountry,
                    targetState,
                    toParams,
                );

                const webSourceChanges = getWebSourceIndustryRequiredChanges(
                    durationChanges.isAllowed,
                    digitalSuiteSelectedDuration,
                    countryChanges.isAllowed,
                    countryChanges.value ?? digitalSuiteSelectedCountry,
                    digitalSuiteSelectedWebSource,
                    targetState,
                    toParams,
                );

                const changedParams = testAndApplyChanges(
                    countryChanges,
                    durationChanges,
                    webSourceChanges,
                    countryChanges.value ?? digitalSuiteSelectedCountry,
                    digitalSuiteSelectedDuration,
                    digitalSuiteSelectedWebSource,
                    toParams?.isDurationChanged,
                    toParams?.isWebSourceChanged,
                );

                if (!isEmpty(changedParams)) {
                    return { params: { ...toParams, ...changedParams } };
                }
            } else {
                // we want to save the filters after the first navigation as selected filters
                saveFilterConsistencyAfterFirstNavigation(targetState, toParams);
            }
        }
    };
};
